<template>
    <div class="event-selector">
        <multiselect-blade-trigger
            v-if="useMultiselectTrigger"
            :selected-targets="selectedTargetsArray"
            @multiselect-blade-trigger-open="isBladeShown = true" />
        <div v-else>
            <div
                v-if="hasSelectedTargets"
                class="selected-targets-wrapper">
                <multiselect-blade-selected-item
                    v-if="selectedTargetsCount === 1"
                    :key="firstSelectedTarget.id"
                    :icon="firstSelectedTarget.icon"
                    :label="firstSelectedTarget.name"
                    :app-icon="firstSelectedTarget.appIcon"
                    :sublabel="firstSelectedTarget.appDisplayName || findAppDisplayName(firstSelectedTarget.appId)" />
                <div
                    v-if="hasSelectedTargets && selectedTargetsCount > 1"
                    class="selected-item-counter">
                    {{ $t('eventSelector.eventsAdded', { count: selectedTargetsCount }) }}
                </div>
                <pendo-button
                    icon="edit-2"
                    :label="$t('Edit')"
                    type="subtle"
                    @click="isBladeShown = true" />
            </div>
            <pendo-button
                v-if="!hasSelectedTargets"
                data-cy="select-event-trigger"
                type="secondary"
                :loading="isAutoApplyingTarget"
                @click="isBladeShown = true">
                {{ eventSelectorActionText }}
            </pendo-button>
        </div>
        <target-selector-blade
            v-if="isBladeShown"
            :action-text="eventSelectorActionText"
            :title="eventSelectorTitle"
            :close-on-mask-click="false"
            :target-limit-by-activity-type="targetLimitByActivityType"
            :all-apps="allApps"
            :all-pages="allPages"
            :all-features="allFeatures"
            :all-track-events="allTrackEvents"
            :all-ai-agents="allAiAgents"
            :all-guides="allGuides"
            :all-core-events="coreEvents"
            :all-product-areas="allProductAreas"
            :recent-pages="recentPages"
            :recent-features="recentFeatures"
            :recent-track-events="recentTrackEvents"
            :recent-guides="recentGuides"
            :recent-agents="recentAgents"
            :target-types-config="computedTargetTypesConfig"
            :loading-target-types="loadingTargetTypes"
            :default-filter-type="defaultFilterType"
            :show-select-all="showSelectAll"
            :disabled-option-id="disabledOptionId"
            :initial-targets="selectedTargetsArray"
            :hide-hover-card="hideHoverCard"
            @apply="onBladeApply"
            @close="handleCloseBlade" />
    </div>
</template>

<script>
import MultiselectBladeSelectedItem from '@/common/multiselect-blade/multiselect-blade-selected-item.vue';
import TargetSelectorBlade from '@/common/target-selector-blade/target-selector-blade.vue';
import { PendoButton } from '@pendo/components';
import MultiselectBladeTrigger from '../../widgets/license-utilization/multiselect-blade-trigger.vue';
import { EventBus } from '@utils/event-bus.js';
import { mapGetters } from 'vuex';

export default {
    name: 'EventSelector',
    components: {
        MultiselectBladeSelectedItem,
        TargetSelectorBlade,
        PendoButton,
        MultiselectBladeTrigger,
    },
    props: {
        selectedTargets: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Selection limits per activity type. Keys are filter types ('app', 'event', 'guide', 'productArea').
         * Values are the maximum number of items that can be selected for that type.
         * If a type is not specified, no limit is enforced (Infinity).
         * Use 1 for single-select mode.
         * @example { app: 1, event: 5, guide: 1, productArea: Infinity }
         */
        targetLimitByActivityType: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Configuration specifying which target types are available.
         * Event types (pages, features, trackEvents, agentEvents) automatically enable the events filter.
         * @example { apps: false, pages: true, features: true, trackEvents: true, agentEvents: false, guides: false, productArea: false }
         */
        targetTypesConfig: {
            type: Object,
            default: () => ({
                apps: false,
                pages: true,
                features: true,
                trackEvents: true,
                agentEvents: false,
                guides: false,
                productArea: false,
            }),
        },
        loadingTargetTypes: {
            type: Object,
            default: () => ({
                apps: true,
                pages: true,
                features: true,
                trackEvents: true,
                agentEvents: true,
                guides: true,
                productArea: true,
            }),
        },
        defaultFilterType: {
            type: String,
            default: 'events',
        },
        allApps: {
            type: Array,
            default: () => [],
        },
        allPages: {
            type: Array,
            default: () => [],
        },
        allFeatures: {
            type: Array,
            default: () => [],
        },
        allTrackEvents: {
            type: Array,
            default: () => [],
        },
        coreEvents: {
            type: Array,
            default: () => [],
        },
        /**
         * Product areas for when productArea is a selectable target type.
         * Should be sourced from Vuex getter groups/list.
         */
        allProductAreas: {
            type: [Array, null],
            default: () => null,
        },
        recentPages: {
            type: Array,
            default: () => [],
        },
        recentFeatures: {
            type: Array,
            default: () => [],
        },
        recentTrackEvents: {
            type: Array,
            default: () => [],
        },
        allGuides: {
            type: [Array, null],
            default: () => null,
        },
        recentGuides: {
            type: Array,
            default: () => [],
        },
        recentAgents: {
            type: Array,
            default: () => [],
        },
        allAiAgents: {
            type: Array,
            default: () => [],
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        disabledOptionId: {
            type: String,
            default: '',
        },
        useMultiselectTrigger: {
            type: Boolean,
            default: false,
        },
        hideHoverCard: {
            type: Boolean,
            default: false,
        },
        isAutoApplyingTarget: {
            type: Boolean,
            default: false,
        },
        title: {
            type: [String, null],
            default: null,
        },
        actionText: {
            type: [String, null],
            default: null,
        },
    },
    emits: ['eventsSelected'],
    data () {
        return {
            isBladeShown: false,
            eventSelectorTitle: '',
            eventSelectorActionText: '',
        };
    },
    computed: {
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
        }),
        hasSelectedTargets () {
            return Object.values(this.selectedTargetsArray).length > 0;
        },
        selectedTargetsArray () {
            return Object.values(this.selectedTargets).filter(Boolean);
        },
        hasAiAgentsSegmentFlag () {
            return this.hasSegmentFlag('aiAgents');
        },
        computedTargetTypesConfig () {
            return {
                ...this.targetTypesConfig,
                agentEvents: this.hasAiAgentsSegmentFlag ? this.targetTypesConfig.agentEvents : false,
            };
        },
        selectedTargetsCount () {
            return this.selectedTargetsArray.length;
        },
        firstSelectedTarget () {
            if (this.hasSelectedTargets) {
                return this.selectedTargetsArray[0];
            }

            return {};
        },
    },
    created () {
        const label = this.$t('eventSelector.Select {singleSelect, plural, one {event} other {events}}', {
            singleSelect: this.targetLimitByActivityType.event === 1 ? 1 : 2,
        });
        this.eventSelectorTitle = this.title !== null ? this.title : label;
        this.eventSelectorActionText = this.actionText !== null ? this.actionText : label;
        this.handleOpenTargetSelectorBlade = () => {
            this.isBladeShown = true;
        };
        EventBus.$on('openTargetSelectorBlade', this.handleOpenTargetSelectorBlade);
    },
    beforeUnmount () {
        EventBus.$off('openTargetSelectorBlade', this.handleOpenTargetSelectorBlade);
    },
    methods: {
        onBladeApply ({ selectedTargets }) {
            if (Object.values(selectedTargets).every((val) => val === false)) {
                this.$emit('eventsSelected', {});
            } else {
                this.$emit('eventsSelected', selectedTargets);
            }
            this.isBladeShown = false;
        },
        handleCloseBlade () {
            this.isBladeShown = false;
        },
        findAppDisplayName (appId) {
            const app = this.allApps.find((app) => app.id === appId);

            return app?.displayName || '';
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.multiselect-blade-selected-item) {
    background: var(--palette-gray-30);
    padding: 4px 8px;
    margin-right: 8px;

    .selected-item-description {
        font-weight: 600;
        color: var(--palette-gray-100);

        .accent-text {
            font-weight: 400;
            color: var(--palette-gray-100);
        }
        .pendo-icon {
            position: relative;
            top: 2px;

            svg {
                stroke: var(--palette-gray-100);
            }
        }
    }
}

.selected-targets-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.selected-item-counter {
    padding: 8px 16px;
    margin-right: 8px;
    background: var(--palette-gray-30);
    color: var(--palette-gray-100);
    font-weight: 600;
    font-family: Inter;
}
</style>
